<template>
  <section class="section vulnerability">
    <div class="chunk">
      <div class="main-section-title">Vulnerability</div>

      <div class="chunk-title">{{ vulnerabilityMetric?.nameAbsolute(this.disasterConfig?.isInternational) }} on {{ dateHeader }}</div>

      <div style="display: flex;">
        <img class="map-img" v-bind:style="reportMapImagesStyle" :src="reportMapViews['vulnerability']" />
        <div class="map-legend">
          <p class="map-legend-title">{{ vulnerabilityMetric?.name(this.disasterConfig?.isInternational) }}</p>
          <VulnerabilityLegend />
          <p class="map-legend-subtitle">{{ vulnerabilityMetric?.source(this.disasterConfig?.isInternational, this.disasterConfig?.censusVintage) }}</p>
        </div>
      </div>
    </div>
    <div class="chunk">
      <div class="chunk-title">Demographics</div>
      <ReportTable
        :titleStyle="'width: 250px !important;'"
        :columnNames="demographicsColumns"
        :rows="demographicsRows"
      />
    </div>

    <div v-if="sortedPowerOutagesTimeseries" class="chunk-title">Power Outages</div>
    <div v-if="sortedPowerOutagesTimeseriesCounties" class="chunk">
        <h5 class="chunk-subtitle">Counties</h5>
        <div class="line-chart-container">
          <div style="width: 150px;" class="line-chart-wrapper" v-for="(dataObj, index) in sortedPowerOutagesTimeseriesCounties" :key="index">
            <PowerOutageLineChart
              :data="dataObj.data"
              :height="80"
              :width="130"
              :circleRadius="2"
              :numTicks="4"
              :displayYTicks="index === 0"
            />
            <p>{{dataObj.polygon_name}}</p>
          </div>
        </div>
    </div>

    <div v-if="sortedPowerOutagesTimeseries" class="chunk">
        <h5 class="chunk-subtitle">Places</h5>
        <div class="line-chart-container">
          <div style="width: 150px;" class="line-chart-wrapper" v-for="(dataObj, index) in sortedPowerOutagesTimeseries" :key="index">
            <PowerOutageLineChart
              :data="dataObj.data"
              :height="80"
              :width="130"
              :circleRadius="2"
              :numTicks="4"
              :displayYTicks="index === 0"
            />
            <p>{{dataObj.polygon_name}}</p>
          </div>
        </div>
    </div>

    <div class="chunk">
      <ReportNotes :id="'vulnerability'"/>
    </div>
  </section>
</template>

<script>
  import { mapState, mapGetters, mapMutations } from 'vuex'

  import * as d3 from 'd3'

  import { settings } from '../../constants/settings'

  import ReportTable from './ReportTable'
  import PowerOutageLineChart from './PowerOutageLineChart'
  import VulnerabilityLegend from './VulnerabilityLegend'
  import ReportNotes from './ReportNotes'


  export default {
    name: 'ReportVulnerabilitySection',

    components: {
      ReportTable,
      PowerOutageLineChart,
      VulnerabilityLegend,
      ReportNotes,
    },

    props: {
      dateHeader: String,
    },

    data() {
      return {
        reportMapImagesStyle: settings.reportMapImages
      }
    },

    computed: {
      ...mapGetters([
        'reportPlaces',
        'reportCounties',
        'selectedCounties',
        'selectedPlaces',
        'numDMEUsers',
        'selectedPowerOutagesTimeseriesRegions',
        'selectedDateString',
      ]),
      ...mapState([
        'reportMapViews',
        'vulnerabilityMetric',
        'regionTypeSelection',
        'disasterConfig'
      ]),

      sortedPowerOutagesTimeseries() {
        const regions = this.regionTypeSelection === "counties" ? this.selectedCounties : this.selectedPlaces
        // eslint-disable-next-line no-undef
        return structuredClone(this.selectedPowerOutagesTimeseriesRegions(regions, this.regionTypeSelection))
          ?.sort((a, b) => {
            return d3.descending(a.max, b.max)
          })
      },

      sortedPowerOutagesTimeseriesCounties() {
        // If you are in places selection, this generates power outage charts for
        // the counties that cointain those places
        if (this.regionTypeSelection === 'counties') return null
        // eslint-disable-next-line no-undef
        return structuredClone(this.selectedPowerOutagesTimeseriesRegions(this.reportCounties, 'counties'))
          ?.sort((a, b) => {
            return d3.descending(a.max, b.max)
          })
      },

      demographicsColumns() {
        const vulnerabilityCols = settings.vulnerabilityMetrics.map(m => {
          return `${m?.name(this.disasterConfig?.isInternational)}${
            m?.id === this.vulnerabilityMetric?.id ? " ▼" : ""}`
        })
        return [
          'Location',
          'Total Pop.',
          this.vulnerabilityMetric?.nameAbsolute(this.disasterConfig?.isInternational),
          ...vulnerabilityCols,
          'Num. Customers Without Power',
          'DME Users',
          'DME Users / 100K Pop.',
        ]
      },

      demographicsRows() {
        const generateDemographicRows = (features, placeType, selectedDateString) => {
          if (!features?.length) return []

          return features.map(feature => {
            let numDMEUsers = this.numDMEUsers(feature)
            let vulnerablePop = feature.properties[this.vulnerabilityMetric?.idAbsolute]
            let numCustomersWithoutPower = feature.properties["customers_without_power_" + selectedDateString]

            return {
              title: feature.properties['NAME'],
              placeType: placeType,
              values: [
                d3.format(",.0f")(feature.properties.totalPopulation),
                vulnerablePop ? d3.format(",.0f")(vulnerablePop) : 'N/A',
                ...settings.vulnerabilityMetrics.map(m => {
                  const value = feature.properties[m?.id]
                  return value >= 0 ? d3.format(".0%")(value) : 'N/A'
                }),
                numCustomersWithoutPower ? d3.format(",.0f")(numCustomersWithoutPower) : 'N/A',
                numDMEUsers >= 0 ? d3.format(".0f")(numDMEUsers) : 'N/A',
                numDMEUsers >= 0 ? d3.format(".4f")(numDMEUsers / 100000) : 'N/A',
              ],
              rawValues: [
                ...settings.vulnerabilityMetrics.map(m => {
                  const value = feature.properties[m?.id]
                  return {value: value, id: m?.id}
                }),
              ],
            }
          })
          .sort((a, b) => {
            return d3.descending(
              a.rawValues.find(d => d.id === this.vulnerabilityMetric?.id)?.value,
              b.rawValues.find(d => d.id === this.vulnerabilityMetric?.id)?.value
            )
          })
        }

        const counties = generateDemographicRows(this.reportCounties, 'counties', this.selectedDateString)
        const places = generateDemographicRows(this.reportPlaces, 'places', this.selectedDateString)

        return [
          ...counties,
          ...places,
        ]
      },

    },

    methods: {
      ...mapMutations([
      ]),
    }
  }
</script>

<style lang="scss">
</style>
